import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { AuthService } from "app/core/auth/auth.service";
import { environment } from "environments/environment";

/**
 * Injeta o access token (Bearer) nas chamadas a API e, quando recebe 401, renova o token
 * via refresh_token e refaz a requisicao. Um unico refresh roda por vez (fila): as demais
 * requisicoes que tomarem 401 durante a renovacao esperam o novo token em vez de disparar
 * novos refresh. Se a renovacao falhar (refresh expirado/revogado), faz logout.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);

  private isRefreshing = false;
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // As chamadas ao proprio auth (login, allowed-organizations, connect/token e o proprio
    // refresh) nao usam o Bearer do usuario. Um 401 ali nao deve disparar refresh (loop).
    if (this.isAuthEndpoint(req.url)) {
      return next.handle(req);
    }

    const token = this.authService.getAccessToken();
    const authReq = token ? this.addToken(req, token) : req;

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401(authReq, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private isAuthEndpoint(url: string): boolean {
    // O dashboard de Controle de Acesso fica sob svc-security-auth mas e uma API autenticada:
    // PRECISA de Bearer e do refresh em 401, ao contrario dos endpoints de credencial/token.
    if (url.includes(`${environment.security_auth_path}/api/access-dashboard`)) {
      return false;
    }

    // Fluxo novo (BFF): login/refresh/validacao de credencial passam pelo svc-security
    // (/svc-security/api/auth/token e /allowed-organizations-for-credential). Sao pre-autenticacao:
    // nao anexar Bearer nem disparar refresh em 401. O restante do svc-security continua autenticado.
    if (
      url.includes("/svc-security/api/auth/token") ||
      url.includes("/svc-security/api/auth/allowed-organizations-for-credential")
    ) {
      return true;
    }

    // Reconhece os dois auth (novo e legado) para nao anexar Bearer nem disparar refresh em 401.
    return (
      url.includes(`${environment.security_auth_path}/`) ||
      url.includes(`${environment.security_auth_legacy_path}/`)
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Ja existe um refresh em andamento: espera o novo token e refaz a requisicao.
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token): token is string => token != null),
        take(1),
        switchMap((token) => next.handle(this.addToken(req, token))),
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.authService.refreshAccessToken().pipe(
      switchMap((tokens) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(tokens.access_token);
        return next.handle(this.addToken(req, tokens.access_token));
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.authService.logout();
        return throwError(() => err);
      }),
    );
  }
}
